// Port of ui/Last Day Logo.dc.html: cracked sun on the horizon + stencil wordmark. Package 4, part M: the wordmark is
// AFTER DAY (nine letters and a space where LAST DAY had eight: the word's box and the logo grow by an eighth).

import { useId } from 'react';

interface Props {
  kind?: 'lockup' | 'stacked' | 'symbol';
  ink?: string;
  accent?: string;
  /** The sun (1. 10. 2026: the brand red, the one red that is neither an action nor danger). */
  sun?: string;
  worn?: boolean;
  small?: boolean;
  className?: string;
  height?: number | string;
}

/** Crimson (1. 10. 2026): the wordmark in the main text colour, the horizon in the bar grey, the sun in the brand red. */
export function Logo({ kind = 'lockup', ink = 'var(--color-text)', accent = 'var(--color-fill)', sun = 'var(--color-brand)', worn = false, small = false, className, height }: Props) {
  const uid = useId().replace(/:/g, '');
  const mSun = `${uid}s`;
  const mWord = `${uid}w`;
  const fid = `${uid}f`;
  const filt = worn ? `url(#${fid})` : undefined;
  const crackBig = '156,112 168,112 150,160 172,184 148,232 160,244 144,244 134,232 158,184 136,160';
  const crackSmall = '154,108 174,108 152,162 178,186 150,236 170,250 138,250 128,236 154,186 128,160';
  const filter = (
    <filter id={fid} x="-2%" y="-5%" width="104%" height="110%">
      <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves={2} seed={4} result="n" />
      <feDisplacementMap in="SourceGraphic" in2="n" scale={4} result="d" />
      <feTurbulence type="fractalNoise" baseFrequency="0.55" numOctaves={2} seed={11} result="s" />
      <feColorMatrix in="s" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 -16 12.4" result="holes" />
      <feComposite in="d" in2="holes" operator="in" />
    </filter>
  );

  if (kind === 'symbol') {
    const barH = small ? 26 : 18;
    return (
      <svg viewBox="0 39 300 300" className={className} style={{ height }} role="img" aria-label="AFTER DAY">
        <defs>
          <mask id={mSun} maskUnits="userSpaceOnUse" x="0" y="0" width="300" height="400">
            <rect x="0" y="0" width="300" height="400" fill="white" />
            <polygon points={small ? crackSmall : crackBig} fill="black" />
          </mask>
        </defs>
        <path d="M30 236 A120 120 0 0 1 270 236 Z" style={{ fill: sun }} mask={`url(#${mSun})`} />
        <rect x="16" y="244" width={small ? 116 : 124} height={barH} style={{ fill: accent }} />
        <rect x={small ? 172 : 162} y="244" width={small ? 112 : 122} height={barH} style={{ fill: accent }} />
      </svg>
    );
  }

  if (kind === 'stacked') {
    return (
      <svg viewBox="0 0 1190 560" className={className} style={{ height }} role="img" aria-label="AFTER DAY">
        <defs>
          <mask id={mSun} maskUnits="userSpaceOnUse" x="0" y="0" width="1100" height="600">
            <rect x="0" y="0" width="1100" height="600" fill="white" />
            <polygon points={crackBig} fill="black" />
          </mask>
          <mask id={mWord} maskUnits="userSpaceOnUse" x="0" y="0" width="1240" height="600">
            <rect x="0" y="0" width="1240" height="600" fill="white" />
            <rect x="0" y="444" width="1240" height="11" fill="black" />
          </mask>
          {filter}
        </defs>
        <g filter={filt}>
          <path transform="translate(445,-86)" d="M30 236 A120 120 0 0 1 270 236 Z" style={{ fill: sun }} mask={`url(#${mSun})`} />
          <g transform="translate(445,-86)">
            <rect x="16" y="244" width="124" height="18" style={{ fill: accent }} />
            <rect x="162" y="244" width="122" height="18" style={{ fill: accent }} />
          </g>
          <text x="10" y="530" style={{ fill: ink }} mask={`url(#${mWord})`} fontFamily="Oswald,sans-serif" fontWeight={700} fontSize={272} textLength={1170} lengthAdjust="spacingAndGlyphs">
            AFTER DAY
          </text>
        </g>
      </svg>
    );
  }

  return (
    <svg viewBox="0 30 1520 250" className={className} style={{ height }} role="img" aria-label="AFTER DAY">
      <defs>
        <mask id={mSun} maskUnits="userSpaceOnUse" x="0" y="0" width="1400" height="320">
          <rect x="0" y="0" width="1400" height="320" fill="white" />
          <polygon points={crackBig} fill="black" />
        </mask>
        <mask id={mWord} maskUnits="userSpaceOnUse" x="0" y="0" width="1540" height="320">
          <rect x="0" y="0" width="1540" height="320" fill="white" />
          <rect x="330" y="176" width="1200" height="11" fill="black" />
        </mask>
        {filter}
      </defs>
      <g filter={filt}>
        <path d="M30 236 A120 120 0 0 1 270 236 Z" style={{ fill: sun }} mask={`url(#${mSun})`} />
        <rect x="16" y="244" width="124" height="18" style={{ fill: accent }} />
        <rect x="162" y="244" width="122" height="18" style={{ fill: accent }} />
        <text x="340" y="262" style={{ fill: ink }} mask={`url(#${mWord})`} fontFamily="Oswald,sans-serif" fontWeight={700} fontSize={272} textLength={1170} lengthAdjust="spacingAndGlyphs">
          AFTER DAY
        </text>
      </g>
    </svg>
  );
}
